import { Create, useForm, useSelect } from "@refinedev/antd";
import { HttpError, useCreate } from "@refinedev/core";
import { Form, Input, Select, Row, Col, Card, App, Divider, Button, Modal, Space, notification } from "antd";
import { PlusOutlined, ArrowLeftOutlined, MailOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { usePageTitle } from "../../hooks";
import { ContactFormList } from "./components/ContactFormList";
import { useState } from "react";

/**
 * ProducerCreate Component
 * Form for creating a new producer with optional inline contacts.
 * On successful creation, navigates to the detail view of the created producer.
 */
export const ProducerCreate = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();

  usePageTitle(t("producers.actions.create", "Crear Productor"));

  const { formProps, form, onFinish } = useForm<any, HttpError, any>({
    action: "create",
    resource: "producers",
    redirect: false,
    onMutationSuccess: (data: any) => {
      message.success(t("producers.messages.created_success", "Productor creado correctamente"));
      const newId = data?.data?.id;
      if (newId) {
        navigate(`/producers/show/${newId}`);
      } else {
        navigate("/producers");
      }
    },
    onMutationError: (error: any) => {
      // Cascada para encontrar el cuerpo DRF: el interceptor de
      // `apiClient` envuelve el error axios en
      // `{ message, status, originalError }`, así que la
      // respuesta real vive en `originalError.response.data`.
      // Dejamos los fallback antiguos por si algún flujo distinto
      // deja el error en la raíz.
      const resp =
        error?.originalError?.response?.data ||
        error?.response?.data ||
        error?.data;
      // Los serializers DRF devuelven un dict `{campo: [msg,...]}` en
      // los 400 por validación (o `{detail: "..."}` para errores más
      // simples). Recorremos ambos formatos y mostramos:
      //   - un `notification.error` prominente arriba a la derecha
      //   - un `message.error` corto abajo
      //   - marcamos cada campo con setFields para pintar el asterisco
      //     rojo debajo del control.
      const fieldErrors: {
        name: string;
        errors: string[];
      }[] = [];
      const messages: string[] = [];

      if (resp && typeof resp === "object") {
        for (const [field, val] of Object.entries(resp)) {
          if (field === "detail" || field === "non_field_errors") {
            const list = Array.isArray(val) ? val : [val];
            list.forEach((m) => messages.push(String(m)));
            continue;
          }
          // Nested (p.ej. `contacts: [{email:[...]}]`). Aplanamos.
          if (Array.isArray(val) && val.some((v) => typeof v === "object" && v !== null)) {
            const flat: string[] = [];
            (val as any[]).forEach((entry, idx) => {
              if (!entry || typeof entry !== "object") return;
              for (const [subField, subVal] of Object.entries(entry)) {
                const subs = Array.isArray(subVal)
                  ? subVal.map(String)
                  : [String(subVal)];
                flat.push(`#${idx + 1} ${subField}: ${subs.join(", ")}`);
              }
            });
            if (flat.length > 0) {
              messages.push(`${field}: ${flat.join(" · ")}`);
            }
            continue;
          }
          const msgs = Array.isArray(val)
            ? val.map((m) => String(m))
            : [String(val)];
          fieldErrors.push({ name: field, errors: msgs });
          messages.push(...msgs);
        }
      }

      if (fieldErrors.length > 0) {
        form.setFields(fieldErrors as any);
      }

      const summary =
        messages.length > 0
          ? messages.slice(0, 3).join(" · ")
          : error?.message ||
            t(
              "producers.messages.create_error",
              "Error al crear el productor",
            );

      message.error(summary);
      notification.error({
        message: t(
          "producers.messages.create_error_title",
          "No se pudo crear el productor",
        ),
        description: summary,
        placement: "topRight",
        duration: 8,
      });
    },
  });

  const { selectProps: portfolioSelectProps } = useSelect({
    resource: "portfolios",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 100 },
  });

  const {
    selectProps: agentSelectProps,
    query: agentQuery,
  } = useSelect({
    resource: "agents",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 100 },
  });

  const {
    selectProps: businessGroupSelectProps,
    query: businessGroupQuery,
  } = useSelect({
    resource: "business-groups",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 100 },
  });

  // Ojo: NO usar el resource "countries" para el pais del productor.
  // Ese endpoint sirve apps.geo.Country cuyos ids divergen del enum
  // COUNTRIES contra el que valida Producer.country (Peru<->Chile
  // invertidos, Uruguay/Italia desplazados, etc). Usamos el endpoint
  // dedicado /producer-countries/ que devuelve la tupla enum tal cual.
  const { selectProps: countrySelectProps } = useSelect({
    resource: "producer-countries",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 100 },
  });

  // Inline creation state
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [bgModalOpen, setBgModalOpen] = useState(false);
  const [agentForm] = Form.useForm();
  const [bgForm] = Form.useForm();

  const { mutate: createAgent } = useCreate();
  const { mutate: createBg } = useCreate();

  const handleCreateAgent = () => {
    agentForm.validateFields().then((values) => {
      createAgent(
        { resource: "agents", values },
        {
          onSuccess: (data) => {
            const newAgent = data.data as any;
            message.success(t("producers.messages.agent_created", "Responsable creado"));
            agentQuery.refetch();
            form.setFieldsValue({ agent: newAgent.id });
            setAgentModalOpen(false);
            agentForm.resetFields();
          },
        },
      );
    });
  };

  const handleCreateBg = () => {
    bgForm.validateFields().then((values) => {
      createBg(
        { resource: "business-groups", values },
        {
          onSuccess: (data) => {
            const newBg = data.data as any;
            message.success(t("producers.messages.bg_created", "Grupo empresarial creado"));
            businessGroupQuery.refetch();
            form.setFieldsValue({ business_group: newBg.id });
            setBgModalOpen(false);
            bgForm.resetFields();
          },
        },
      );
    });
  };

  const handleSubmit = async (values: any) => {
    // Sanitize CIF: keep only alphanumeric and hyphens
    const cleanedCif = values.cif
      ? values.cif.replace(/[^A-Za-z0-9\-]/g, "")
      : values.cif;

    const contacts = (values.contacts || []).map((contact: any) => ({
      ...contact,
      email: contact.email || null,
      main_phone: contact.main_phone
        ? `${contact.phone_prefix || "+34"} ${contact.main_phone}`
        : null,
      secondary_phone: contact.secondary_phone
        ? `${contact.phone_prefix || "+34"} ${contact.secondary_phone}`
        : null,
      identification_number: contact.identification_number || null,
      position_id: contact.position_id || null,
    }));

    // `onFinish` re-lanza la promesa rechazada del mutate, con lo que
    // sin este try/catch aparece "Uncaught (in promise)" en consola
    // aunque el toast/notification ya lo esté informando via
    // `onMutationError`. Silenciamos aquí — la UI ya está cubierta.
    try {
      await onFinish({
        ...values,
        cif: cleanedCif,
        contacts: contacts.length > 0 ? contacts : undefined,
      });
    } catch {
      // handled by onMutationError
    }
  };

  const selectFilter = (input: string, option: any) =>
    String(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const addNewDropdown = (menu: React.ReactNode, onClick: () => void, label: string) => (
    <>
      {menu}
      <Divider style={{ margin: "8px 0" }} />
      <Button type="link" icon={<PlusOutlined />} onClick={onClick} block>
        {label}
      </Button>
    </>
  );

  return (
    <Create
      title={t("producers.actions.create", "Crear Productor")}
      saveButtonProps={{ onClick: () => form.submit() }}
      headerButtons={
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/producers")}>
          {t("common.back", "Volver")}
        </Button>
      }
      footerButtons={
        <Space>
          <Button onClick={() => navigate("/producers")}>
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button type="primary" onClick={() => form.submit()}>
            {t("common.save", "Guardar")}
          </Button>
        </Space>
      }
    >
      <Form {...formProps} layout="vertical" onFinish={handleSubmit}>
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("producers.fields.name", "Nombre")}
                name="name"
                rules={[{ required: true, message: t("producers.messages.name_required", "El nombre es obligatorio") }]}
              >
                <Input placeholder={t("producers.placeholders.name", "Nombre del productor")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={t("producers.fields.cif", "CIF / RUC")} name="cif">
                <Input placeholder={t("producers.placeholders.cif", "Ej: B12345678")} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label={t("producers.fields.portfolio", "Cartera")}
                name="portfolio"
                rules={[
                  {
                    required: true,
                    message: t(
                      "producers.messages.portfolio_required",
                      "La cartera es obligatoria",
                    ),
                  },
                ]}
              >
                <Select
                  {...portfolioSelectProps}
                  placeholder={t("producers.placeholders.portfolio", "Seleccionar cartera")}
                  allowClear
                  showSearch
                  filterOption={selectFilter}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label={t("producers.fields.agent", "Responsable")}
                name="agent"
                rules={[
                  {
                    required: true,
                    message: t(
                      "producers.messages.agent_required",
                      "El responsable es obligatorio",
                    ),
                  },
                ]}
              >
                <Select
                  {...agentSelectProps}
                  placeholder={t("producers.placeholders.agent", "Seleccionar responsable")}
                  allowClear
                  showSearch
                  filterOption={selectFilter}
                  dropdownRender={(menu) =>
                    addNewDropdown(menu, () => setAgentModalOpen(true), t("producers.actions.new_agent", "Nuevo responsable"))
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label={t("producers.fields.country", "País")}
                name="country"
                rules={[
                  {
                    required: true,
                    message: t(
                      "producers.messages.country_required",
                      "El país es obligatorio",
                    ),
                  },
                ]}
              >
                <Select
                  {...countrySelectProps}
                  placeholder={t("producers.placeholders.country", "Seleccionar país")}
                  allowClear
                  showSearch
                  filterOption={selectFilter}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item label={t("producers.fields.business_group", "Grupo Empresarial")} name="business_group">
                <Select
                  {...businessGroupSelectProps}
                  placeholder={t("producers.placeholders.business_group", "Seleccionar grupo empresarial")}
                  allowClear
                  showSearch
                  filterOption={selectFilter}
                  dropdownRender={(menu) =>
                    addNewDropdown(menu, () => setBgModalOpen(true), t("producers.actions.new_business_group", "Nuevo grupo empresarial"))
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title={t("contacts.title", "Contactos del Productor")} style={{ marginBottom: 24 }}>
          <ContactFormList />
        </Card>
      </Form>

      {/* Modal: Crear Responsable */}
      <Modal
        title={t("producers.actions.new_agent", "Nuevo responsable")}
        open={agentModalOpen}
        onOk={handleCreateAgent}
        onCancel={() => { setAgentModalOpen(false); agentForm.resetFields(); }}
        confirmLoading={false}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
      >
        <Form form={agentForm} layout="vertical">
          <Form.Item name="name" label={t("common.name", "Nombre")} rules={[{ required: true, message: t("common.required", "Campo obligatorio") }]}>
            <Input placeholder={t("producers.placeholders.agent_name", "Nombre del responsable")} />
          </Form.Item>
          <Form.Item name="email" label={t("common.email", "Email")} rules={[{ required: true, type: "email", message: t("common.email_invalid", "Email no válido") }]}>
            <Input prefix={<MailOutlined style={{ color: "#bfbfbf" }} />} placeholder={t("producers.placeholders.agent_email", "email@ejemplo.com")} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal: Crear Grupo Empresarial */}
      <Modal
        title={t("producers.actions.new_business_group", "Nuevo grupo empresarial")}
        open={bgModalOpen}
        onOk={handleCreateBg}
        onCancel={() => { setBgModalOpen(false); bgForm.resetFields(); }}
        confirmLoading={false}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
      >
        <Form form={bgForm} layout="vertical">
          <Form.Item name="name" label={t("common.name", "Nombre")} rules={[{ required: true, message: t("common.required", "Campo obligatorio") }]}>
            <Input placeholder={t("producers.placeholders.bg_name", "Nombre del grupo empresarial")} />
          </Form.Item>
          <Form.Item name="country" label={t("producers.fields.country", "País")} rules={[{ required: true, message: t("common.required", "Campo obligatorio") }]}>
            <Select
              {...countrySelectProps}
              placeholder={t("producers.placeholders.country", "Seleccionar país")}
              allowClear
              showSearch
              filterOption={selectFilter}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Create>
  );
};
